import { type UseMutationResult, useMutation } from '@tanstack/react-query';

import api from '@/api/axios';
import { queryClient } from '@/app/config/react-query';
import { useErrorHandling } from '@/hooks/useErrorHandling';
import { useAuditStore } from '@/store/auditStore';
import type { UploadDocumentParams } from '@/types/documentUpload';

const addAuditCaseDocument = async ({
  auditCaseUuid,
  moduleId,
  docDefinitionId,
  file,
  displayName,
  language,
}: UploadDocumentParams): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('moduleId', moduleId);
  formData.append('docDefinitionId', docDefinitionId);
  formData.append('fileName', file.name);
  if (displayName) {
    formData.append('displayName', displayName);
  }

  if (language) {
    formData.append('language', language);
  }

  await api.patch(`/audit-cases/${auditCaseUuid}/documents`, formData);
};

export const useAddAuditCaseDocument = (): UseMutationResult<
  void,
  unknown,
  UploadDocumentParams
> => {
  const handleErrors = useErrorHandling();
  const incrementPendingActions = useAuditStore((state) => state.incrementPendingActions);
  const decrementPendingActions = useAuditStore((state) => state.decrementPendingActions);

  return useMutation({
    mutationFn: addAuditCaseDocument,
    onMutate: () => incrementPendingActions(),
    onSuccess: (_, { auditCaseUuid, moduleId }) => {
      queryClient.invalidateQueries({
        queryKey: ['audit-case-documents', auditCaseUuid, moduleId],
      });
      queryClient.invalidateQueries({
        queryKey: ['audit-case-document-definitions', auditCaseUuid, moduleId],
      });
    },
    onError: (error: unknown) => {
      handleErrors(error);
    },
    onSettled: () => decrementPendingActions(),
  });
};
